import { HeadlineH3Bold } from '@nect/ui/component/headline';
import { TextBodyRegularParagraph } from '@nect/ui/component/text';
import { Color } from '@nect/ui/theme/color';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { LeftArrowSVG } from '../assets/LeftArrowSVG';
import { QuestionIconSVG } from '../assets/question-icon';
import { Card } from '../components/faq/Card';
import { FaqStyled } from '../components/faq/Faq.styled';
import { Footer } from '../components/Footer/Footer';
import { Header } from '../components/Header/Header';
import { routes } from '../constants/constants';
import { extractUrlParam } from '../helper/extractUrlParam';
import { getFaqContent } from '../helper/getFaqContent';
import { useLocation } from '../react-router-hooks';

export const FaqScreen = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { location } = useLocation();
  const [paramsState, setParamsState] = useState<any>();
  const faqContent = getFaqContent();

  useEffect(() => {
    const element = document.getElementById(id);

    if (!element) {
      return;
    }

    const top = element.offsetTop - 100;
    window.scroll({ top, behavior: 'auto' });
  }, [id]);

  useEffect(() => {
    if (!location.state || !location.state.uuid) {
      const url = location.search;
      setParamsState({
        uuid: extractUrlParam({ url, type: 'uuid' }),
        model: extractUrlParam({ url, type: 'model' }),
        appVersion: extractUrlParam({ url, type: 'appVersion' }),
        systemVersion: extractUrlParam({ url, type: 'systemVersion' }),
        customer: extractUrlParam({ url, type: 'customer' })
      });
    } else {
      setParamsState(location.state);
    }
  }, [location]);

  return (
    <div className="root">
      <Header />
      <div id="main" className="main">
        <FaqStyled.NavigationMainContainer>
          <FaqStyled.NavigationContainer>
            <img src={require('../assets/faqhome.svg')} />
            <FaqStyled.NavigationText
              to={{ pathname: routes.HOME, state: paramsState }}
            >
              {t('Start ')}
            </FaqStyled.NavigationText>
          </FaqStyled.NavigationContainer>
        </FaqStyled.NavigationMainContainer>

        <FaqStyled.ContainerMain id="main" style={{ marginTop: 60 }}>
          <FaqStyled.Container>
            <HeadlineH3Bold>{t('General FAQs')}</HeadlineH3Bold>
            <TextBodyRegularParagraph
              style={{ marginTop: 8, marginBottom: 40 }}
              color={Color.grey600}
            >
              {t(
                'Choose a category to quickly find the answers to your questions.'
              )}
            </TextBodyRegularParagraph>

            <FaqStyled.Title id="general">{t('General')}</FaqStyled.Title>
            <FaqStyled.CardsContainer>
              <Card
                icon={require('../assets/faq-flow.svg')}
                title={t('Process')}
                message={t(
                  'Step by step - Here you will find instructions for the identification.'
                )}
                to={{
                  pathname: faqContent.flow.subCategories
                    ? routes.FAQSUBCATEGORY.replace(':idCategory', 'flow')
                    : routes.FAQCONTENT.replace(':idCategory', 'flow').replace(
                        ':idSubCategory',
                        'general'
                      ),
                  state: paramsState
                }}
              />
              <Card
                icon={require('../assets/faq-mistake.svg')}
                title={t('FAQs')}
                message={t(
                  'Find solutions for the most common identification problems.'
                )}
                to={{
                  pathname: routes.FAQSUBCATEGORY.replace(
                    ':idCategory',
                    'commonmistakes'
                  ),
                  state: paramsState
                }}
              />
            </FaqStyled.CardsContainer>
            <FaqStyled.CardsContainer>
              <Card
                icon={require('../assets/faq-why-nect.svg')}
                title={t('Why Nect?')}
                message={t(
                  'Identify yourself in a user-friendly, fast and secure way. Learn more about Nect.'
                )}
                to={{
                  pathname: faqContent.whynect.subCategories
                    ? routes.FAQSUBCATEGORY.replace(':idCategory', 'whynect')
                    : routes.FAQCONTENT.replace(
                        ':idCategory',
                        'whynect'
                      ).replace(':idSubCategory', 'general'),
                  state: paramsState
                }}
              />
              <Card
                icon={require('../assets/faq-how-work.svg')}
                title={t('Made by Nect')}
                message={t('Learn more about the technology behind Nect.')}
                to={{
                  pathname: faqContent.howdoesitwork.subCategories
                    ? routes.FAQSUBCATEGORY.replace(
                        ':idCategory',
                        'howdoesitwork'
                      )
                    : routes.FAQCONTENT.replace(
                        ':idCategory',
                        'howdoesitwork'
                      ).replace(':idSubCategory', 'general'),
                  state: paramsState
                }}
              />
            </FaqStyled.CardsContainer>
            <FaqStyled.Title id="nectApp">{t('Nect App')}</FaqStyled.Title>
            <FaqStyled.CardsContainer>
              <Card
                icon={require('../assets/faq-download-app.svg')}
                title={t('App download')}
                message={t(
                  'Here you can find instructions for downloading the Nect app.'
                )}
                to={{
                  pathname: faqContent.downloadapp.subCategories
                    ? routes.FAQSUBCATEGORY.replace(
                        ':idCategory',
                        'downloadapp'
                      )
                    : routes.FAQCONTENT.replace(
                        ':idCategory',
                        'downloadapp'
                      ).replace(':idSubCategory', 'general'),
                  state: paramsState
                }}
              />
              <Card
                icon={require('../assets/faq-accepted-docs.svg')}
                title={t('Accepted Documents')}
                message={t(
                  'You can use these documents for your identification.'
                )}
                to={{
                  pathname: faqContent.documents.subCategories
                    ? routes.FAQSUBCATEGORY.replace(':idCategory', 'documents')
                    : routes.FAQCONTENT.replace(
                        ':idCategory',
                        'documents'
                      ).replace(':idSubCategory', 'general'),
                  state: paramsState
                }}
              />
            </FaqStyled.CardsContainer>
            <FaqStyled.CardsContainer>
              <Card
                icon={require('../assets/faq-system-req.svg')}
                title={t('Compatibility')}
                message={t(
                  'The Nect App is compatible with these operating systems.'
                )}
                to={{
                  pathname: faqContent.systemrequirements.subCategories
                    ? routes.FAQSUBCATEGORY.replace(
                        ':idCategory',
                        'systemrequirements'
                      )
                    : routes.FAQCONTENT.replace(
                        ':idCategory',
                        'systemrequirements'
                      ).replace(':idSubCategory', 'general'),
                  state: paramsState
                }}
              />
            </FaqStyled.CardsContainer>
            <FaqStyled.Title id="nectWebsite">
              {t('Nect Website')}
            </FaqStyled.Title>
            <FaqStyled.CardsContainer>
              <Card
                icon={require('../assets/faq-jump-page.svg')}
                title={t('Jump Page')}
                message={t(
                  'We explain what the Jump Page is and how it works.'
                )}
                to={{
                  pathname: faqContent.jumppage.subCategories
                    ? routes.FAQSUBCATEGORY.replace(':idCategory', 'jumppage')
                    : routes.FAQCONTENT.replace(
                        ':idCategory',
                        'jumppage'
                      ).replace(':idSubCategory', 'general'),
                  state: paramsState
                }}
              />
              <Card
                icon={require('../assets/faq-qr.svg')}
                title={t('QR-Code')}
                message={t('See where to find the QR Code and how to use it.')}
                to={{
                  pathname: faqContent.qrcode.subCategories
                    ? routes.FAQSUBCATEGORY.replace(':idCategory', 'qrcode')
                    : routes.FAQCONTENT.replace(
                        ':idCategory',
                        'qrcode'
                      ).replace(':idSubCategory', 'general'),
                  state: paramsState
                }}
              />
            </FaqStyled.CardsContainer>
            <FaqStyled.Title id="dataSecurity">
              {t('Data protection')}
            </FaqStyled.Title>
            <FaqStyled.CardsContainer>
              <Card
                icon={require('../assets/faq-gdpr.svg')}
                title={t('GDPR')}
                message={t(
                  'Your data is processed and protected by Nect with full GDPR compliance.'
                )}
                to={{
                  pathname: faqContent.gdpr.subCategories
                    ? routes.FAQSUBCATEGORY.replace(':idCategory', 'gdpr')
                    : routes.FAQCONTENT.replace(':idCategory', 'gdpr').replace(
                        ':idSubCategory',
                        'general'
                      ),
                  state: paramsState
                }}
              />
              <Card
                icon={require('../assets/faq-erase-data.svg')}
                title={t('Delete your data')}
                message={t(
                  'How Nect handles data deletion requests and how to apply.'
                )}
                to={{
                  pathname: faqContent.erasemydata.subCategories
                    ? routes.FAQSUBCATEGORY.replace(
                        ':idCategory',
                        'erasemydata'
                      )
                    : routes.FAQCONTENT.replace(
                        ':idCategory',
                        'erasemydata'
                      ).replace(':idSubCategory', 'general'),
                  state: paramsState
                }}
              />
            </FaqStyled.CardsContainer>
            <FaqStyled.CardsContainer>
              <Card
                icon={require('../assets/faq-caseid.svg')}
                title={t('Case ID')}
                message={t(
                  'Learn what the Case ID (Universally Unique Identifier) number is needed for.'
                )}
                to={{
                  pathname: faqContent.caseid.subCategories
                    ? routes.FAQSUBCATEGORY.replace(':idCategory', 'caseid')
                    : routes.FAQCONTENT.replace(
                        ':idCategory',
                        'caseid'
                      ).replace(':idSubCategory', 'general'),
                  state: paramsState
                }}
              />
            </FaqStyled.CardsContainer>
            <FaqStyled.Title id="tips">{t('Tips')}</FaqStyled.Title>
            <FaqStyled.CardsContainer>
              <Card
                icon={require('../assets/faq-record-doc.svg')}
                title={t('Record documents')}
                message={t('We show you how to record your document.')}
                to={{
                  pathname: faqContent.recorddocumets.subCategories
                    ? routes.FAQSUBCATEGORY.replace(
                        ':idCategory',
                        'recorddocumets'
                      )
                    : routes.FAQCONTENT.replace(
                        ':idCategory',
                        'recorddocumets'
                      ).replace(':idSubCategory', 'general'),
                  state: paramsState
                }}
              />
              <Card
                icon={require('../assets/faq-record-selfie.svg')}
                title={t('Record a Selfie-Video')}
                message={t('We show you how to record a Selfie-Video.')}
                to={{
                  pathname: faqContent.recordselfie.subCategories
                    ? routes.FAQSUBCATEGORY.replace(
                        ':idCategory',
                        'recordselfie'
                      )
                    : routes.FAQCONTENT.replace(
                        ':idCategory',
                        'recordselfie'
                      ).replace(':idSubCategory', 'general'),
                  state: paramsState
                }}
              />
            </FaqStyled.CardsContainer>
            <FaqStyled.CardsContainer>
              <Card
                icon={require('../assets/faq-uuid.svg')}
                title={t('Find Case ID')}
                message={t('We show you where to find your Case ID.')}
                to={{
                  pathname: faqContent.ididentifier.subCategories
                    ? routes.FAQSUBCATEGORY.replace(
                        ':idCategory',
                        'ididentifier'
                      )
                    : routes.FAQCONTENT.replace(
                        ':idCategory',
                        'ididentifier'
                      ).replace(':idSubCategory', 'general'),
                  state: paramsState
                }}
              />
            </FaqStyled.CardsContainer>
            <FaqStyled.Box>
              <QuestionIconSVG />

              <FaqStyled.BoxTitle style={{ marginTop: 8 }}>
                {t('Couldn’t find the answer to your question?')}
              </FaqStyled.BoxTitle>

              <FaqStyled.BoxMessage>
                {t(
                  'Please contact us by using the button below and our support team will be on their way.'
                )}
              </FaqStyled.BoxMessage>
              <FaqStyled.BlueButtonContainer>
                <FaqStyled.BlueButtonLink
                  to={{ pathname: routes.HELPFORM, state: paramsState }}
                >
                  {t('Ask for help')}
                  <LeftArrowSVG />
                </FaqStyled.BlueButtonLink>
              </FaqStyled.BlueButtonContainer>
            </FaqStyled.Box>
          </FaqStyled.Container>
        </FaqStyled.ContainerMain>
      </div>
      <Footer />
    </div>
  );
};
